import { Fragment, useEffect, useState } from "react";
import {
  IconArrowLeft,
  IconArrowRight,
  IconPillarPersonality,
  IconPillarPersonalityDark,
  IconPillarPreference,
  IconPillarPreferenceDark,
  IconPillarProject,
  IconPillarProjectDark,
} from "../components/Icons";
import { PillarCard } from "../components/PillarCard";
import { pillars } from "../data";
import { useIsMobile } from "../hooks/useIsMobile";

const pillarIcons = {
  fingerprint: {
    light: IconPillarPersonality,
    dark: IconPillarPersonalityDark,
  },
  heart: {
    light: IconPillarPreference,
    dark: IconPillarPreferenceDark,
  },
  clipboard: {
    light: IconPillarProject,
    dark: IconPillarProjectDark,
  },
} as const;

type PillarsSectionProps = {
  activePillar: number;
  onSelect: (index: number) => void;
};

export const PillarsSection = ({
  activePillar,
  onSelect,
}: PillarsSectionProps) => {
  const isMobile = useIsMobile(960);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const safeIndex = Math.max(0, Math.min(activePillar, pillars.length - 1));
  const active = pillars[safeIndex];
  const ActiveIcon = pillarIcons[active.icon].light;

  useEffect(() => {
    if (!isMobile) {
      setIsDialogOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isDialogOpen || !isMobile) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsDialogOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isDialogOpen, isMobile]);

  const handleSelect = (index: number) => {
    onSelect(index);
    if (isMobile) {
      setIsDialogOpen(true);
    }
  };

  const handlePrev = () => {
    const nextIndex =
      (safeIndex - 1 + pillars.length) % pillars.length;
    onSelect(nextIndex);
  };

  const handleNext = () => {
    const nextIndex = (safeIndex + 1) % pillars.length;
    onSelect(nextIndex);
  };

  const panelContent = (
    <>
      <div className="sg-pillars__panel-head">
        <div className="sg-pillars__panel-icon">
          <ActiveIcon size={50} />
        </div>
        <div>
          <div className="sg-pillars__step">Step {safeIndex + 1}</div>
          <h3 className="sg-pillars__panel-title">{active.title}</h3>
        </div>
      </div>

      {isMobile ? (
        <div className="sg-pillars__panel-grid sg-pillars__panel-grid--stacked">
          <div>
            <div className="sg-pillars__panel-label">Homeowner Questions</div>
            {active.homeownerQuestions.map((question) => (
              <div key={question} className="sg-question">
                {question}
              </div>
            ))}
          </div>
          <div>
            <div className="sg-pillars__panel-label">
              Interior Designer Questions
            </div>
            {active.designerQuestions.map((question) => (
              <div key={question} className="sg-question">
                {question}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="sg-pillars__panel-grid sg-pillars__panel-grid--paired">
          <div className="sg-pillars__panel-label">Homeowner Questions</div>
          <div className="sg-pillars__panel-label">
            Interior Designer Questions
          </div>
          {Array.from({
            length: Math.max(
              active.homeownerQuestions.length,
              active.designerQuestions.length
            ),
          }).map((_, index) => {
            const homeownerQuestion = active.homeownerQuestions[index];
            const designerQuestion = active.designerQuestions[index];

            return (
              <Fragment key={`${active.title}-row-${index}`}>
                {homeownerQuestion ? (
                  <div className="sg-question">{homeownerQuestion}</div>
                ) : (
                  <div
                    className="sg-question sg-question--empty"
                    aria-hidden="true"
                  />
                )}
                {designerQuestion ? (
                  <div className="sg-question">{designerQuestion}</div>
                ) : (
                  <div
                    className="sg-question sg-question--empty"
                    aria-hidden="true"
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <section className="sg-section sg-pillars">
      <div className="sg-container">
        <div className="sg-section__header">
          <span className="sg-kicker">The Smart-Guild Difference</span>
          <h2 className="sg-section__title sg-section__title--magenta">
            We Match on 40+ Variables
          </h2>
          <p className="sg-section__subtitle">
            Within three essential dimensions:
          </p>
        </div>

        <div className="sg-priority">
          <span className="sg-priority__label">Matching Priority:</span>
          <div className="sg-priority__items">
            {pillars.map((pillar, index) => (
              <span key={pillar.title} className="sg-priority__item-wrap">
                <button
                  type="button"
                  className={[
                    "sg-priority__item",
                    index === safeIndex ? "is-active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleSelect(index)}
                >
                  {pillar.priorityLabel}
                </button>
                {index < pillars.length - 1 ? (
                  <IconArrowRight size={14} className="sg-priority__arrow" />
                ) : null}
              </span>
            ))}
          </div>
        </div>

        <div className="sg-pillars__grid">
          {pillars.map((pillar, index) => {
            const Icon =
              isMobile || index === safeIndex
                ? pillarIcons[pillar.icon].dark
                : pillarIcons[pillar.icon].light;
            return (
              <PillarCard
                key={pillar.title}
                icon={<Icon size={50} />}
                title={pillar.title}
                description={pillar.description}
                linkText={pillar.linkText}
                active={index === safeIndex}
                onClick={() => handleSelect(index)}
              />
            );
          })}
        </div>

        <div className="sg-pillars__panel">{panelContent}</div>
      </div>

      {isMobile && isDialogOpen ? (
        <div
          className="sg-pillars__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sg-pillars-dialog-title"
        >
          <button
            type="button"
            className="sg-pillars__dialog-backdrop"
            aria-label="Close matching priority dialog"
            onClick={() => setIsDialogOpen(false)}
          />
          <div className="sg-pillars__dialog-card">
            <div className="sg-pillars__dialog-header">
              <span
                className="sg-pillars__dialog-title"
                id="sg-pillars-dialog-title"
              >
                Matching Priority
              </span>
              <button
                type="button"
                className="sg-pillars__dialog-close"
                aria-label="Close matching priority dialog"
                onClick={() => setIsDialogOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="sg-pillars__dialog-body">
              <div className="sg-pillars__panel sg-pillars__panel--dialog">
                {panelContent}
              </div>
              <div className="sg-pillars__dialog-nav">
                <button
                  type="button"
                  className="sg-pillars__dialog-nav-btn"
                  onClick={handlePrev}
                  aria-label="Previous matching priority"
                >
                  <IconArrowLeft size={18} />
                </button>
                <button
                  type="button"
                  className="sg-pillars__dialog-nav-btn"
                  onClick={handleNext}
                  aria-label="Next matching priority"
                >
                  <IconArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};
